import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { OverlapService } from 'src/app/shared/service/overlapService/overlap.service';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';

export interface PortConsumptionReportDownloadData {
  oldReportUrl?: string;
  newReportUrl?: string;
  vesselName?: string;
}

const PROCESSING_MESSAGES = [
  'Preparing Report...',
  'Downloading Report...',
  'Please wait...',
] as const;

@Component({
  selector: 'app-port-consumption-report-download-popup',
  templateUrl: './port-consumption-report-download-popup.component.html',
  styleUrls: ['./port-consumption-report-download-popup.component.scss'],
})
export class PortConsumptionReportDownloadPopupComponent implements OnDestroy {
  isProcessing = false;
  processingMessage: string = PROCESSING_MESSAGES[0];

  readonly hasOldReport: boolean;
  readonly hasNewReport: boolean;

  private processingMessageTimer: ReturnType<typeof setInterval> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private requestInFlight = false;

  constructor(
    private dialogRef: MatDialogRef<PortConsumptionReportDownloadPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PortConsumptionReportDownloadData,
    private pRequestFormService: PortRequestFormServiceService,
    private overlayService: OverlapService,
    private toastr: ToastrService,
  ) {
    this.hasOldReport = !!this.normalizeUrl(data?.oldReportUrl);
    this.hasNewReport = !!this.normalizeUrl(data?.newReportUrl);
  }

  ngOnDestroy(): void {
    this.clearProcessingMessageTimer();
    this.clearCloseTimer();
  }

  close(): void {
    if (this.isProcessing) {
      return;
    }
    this.dialogRef.close();
  }

  downloadOldReport(): void {
    this.downloadReport(this.data?.oldReportUrl);
  }

  downloadNewReport(): void {
    this.downloadReport(this.data?.newReportUrl);
  }

  private downloadReport(url: string | undefined): void {
    const s3Url = this.normalizeUrl(url);
    if (!s3Url || this.requestInFlight) {
      return;
    }

    this.requestInFlight = true;
    this.startProcessing();

    this.pRequestFormService.getReport({ s3_url: s3Url }).subscribe({
      next: (pdfBlob: Blob) => {
        this.overlayService.showPdf(pdfBlob);
        this.stopProcessing();
        this.toastr.success(
          'Port Consumption Report downloaded successfully.',
          'Success',
        );
        this.closeTimer = setTimeout(() => {
          this.dialogRef.close();
        }, 400);
      },
      error: () => {
        this.stopProcessing();
        this.toastr.error(
          'Unable to download report. Please try again.',
          'Error',
        );
      },
    });
  }

  private startProcessing(): void {
    this.isProcessing = true;
    this.dialogRef.disableClose = true;
    this.processingMessage = PROCESSING_MESSAGES[0];

    let step = 0;
    this.clearProcessingMessageTimer();
    this.processingMessageTimer = setInterval(() => {
      step = (step + 1) % PROCESSING_MESSAGES.length;
      this.processingMessage = PROCESSING_MESSAGES[step];
    }, 1400);
  }

  private stopProcessing(): void {
    this.isProcessing = false;
    this.requestInFlight = false;
    this.dialogRef.disableClose = false;
    this.clearProcessingMessageTimer();
  }

  private clearProcessingMessageTimer(): void {
    if (this.processingMessageTimer) {
      clearInterval(this.processingMessageTimer);
      this.processingMessageTimer = null;
    }
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private normalizeUrl(url?: string): string {
    return typeof url === 'string' ? url.trim() : '';
  }
}
